<script lang="ts" setup>
import { format } from 'date-fns'
import { debounce } from 'throttle-debounce'
import { useField } from 'vee-validate'
import { vMaska } from 'maska'

import { useRoute } from '#app'

/* Utils */
import { codeValidator } from '@/utils/rulesOfValidate'

import { useLoginStore } from '~/store/login/login'
import { useUserStore } from '~/store/user/user'
import { useCityStore } from '~/store/city/city'
import { CurrentForm, DeliveryType } from '@/store/login/login.types'
import {
  BadRequestError,
  Success,
  TooManyRequestError,
  UnprocessableEntityError,
} from '~/api/results'
import { useAppStore } from '~/store/app/app'

const props = defineProps({
  signInRedirectPath: {
    type: String,
    required: false,
    default: null,
  },
})

const { showSnackbar } = useAppStore()
const { optionsPhone } = useCityStore()
const loginStore = useLoginStore()
const userStore = useUserStore()

const { $metrics } = useNuxtApp()

const { name: routeName } = useRoute()

const emit = defineEmits([
  'codeConfirmedByNamedUser',
  'codeConfirmedByUnknowUser',
])

const codeInfo = computed(() => userStore.code)

const form = ref({
  code: {
    id: 'code',
    label: 'Код подтверждения *',
    value: '',
    rules: 'required',
  },
})
const confirmCode = ref({
  inProgress: false,
  confirmed: false,
})

const { value, setErrors, errorMessage } = useField('code', (value: string) =>
  codeValidator(value),
)

const requestCodeState = computed(() => loginStore.actions.requestCode)

const verificationCodeLength = ref(
  '#'.repeat(codeInfo.value.code_length).length || 4,
)

const verificationCode = ref('#'.repeat(codeInfo.value.code_length))

// Маска для ввода кода
const optionsCode = ref({
  mask: verificationCode,
  eager: true,
})

const phoneWithoutMask = computed(() =>
  loginStore.getPhoneWithoutMask(optionsPhone.code),
)
const formatTime = computed(() =>
  format(new Date(0, 0, 0, 0, 0, requestCodeState.value.timer), 'mm:ss'),
)

const changePhoneHandler = () => {
  loginStore.changeCurrentForm(CurrentForm.REQUEST_CODE)
}

const repeatRequestCodeHandler = async () => {
  loginStore.setRequestCodeState({ inProgress: true })

  const result = await userStore.requestCode({
    phone: phoneWithoutMask.value,
    ui_element: routeName || '',
  })

  if (result instanceof Success) {
    userStore.setCode(result?.data)

    loginStore.startRequestCodeTimer(codeInfo.value.waitSeconds)
  } else if (result instanceof BadRequestError) {
    showSnackbar({
      message: result.message,
    })
  } else if (result instanceof TooManyRequestError) {
    showSnackbar({
      message: result.message,
    })

    loginStore.startRequestCodeTimer(result.timeout)
  } else {
    showSnackbar({
      message: 'Что-то пошло не так, попробуйте позже',
    })
  }

  loginStore.setRequestCodeState({ inProgress: false })
}

// Смотрим за изменением состояния кода, и вызываем подтверждение в случае заполнения
watch(value, (value: string) => (form.value.code.value = value || ''))
watch(form.value.code, (code) => {
  if (code.value.length === verificationCodeLength.value) {
    confirmCodeHandler()
  }
})

const confirmCodeHandler = debounce(500, async () => {
  if (form.value.code.value.length !== verificationCodeLength.value) return

  confirmCode.value.inProgress = true

  const result = await userStore.confirmCode({
    code: form.value.code.value,
    phone: phoneWithoutMask.value,
    ui_element: routeName || '',
  })

  if (result instanceof Success) {
    confirmCode.value.confirmed = true

    const userInfo = userStore.info

    await userStore.getStatistic()

    $metrics?.eventSendGoal({ ym: 'other_auth' })

    setTimeout(() => {
      if (userInfo?.fullName) {
        if (props.signInRedirectPath)
          window.location.replace(props.signInRedirectPath)

        emit('codeConfirmedByNamedUser')
      } else {
        loginStore.changeCurrentForm(CurrentForm.SET_NAME)

        emit('codeConfirmedByUnknowUser')
      }
    }, 1000)
  } else if (result instanceof UnprocessableEntityError) {
    setErrors([result.data.detail])
  } else if (result instanceof BadRequestError) {
    setErrors([result.data])
  } else if (result instanceof TooManyRequestError) {
    showSnackbar({
      message: result.message,
    })
  } else {
    showSnackbar({
      message: 'Что-то пошло не так, попробуйте позже',
    })
  }

  confirmCode.value.inProgress = false
})
</script>

<template>
  <v-form class="form" @submit.prevent>
    <h1 class="confirm-title">
      <span v-if="codeInfo.deliveredBy === DeliveryType.CALL">
        Поступит звонок, автоответчик продиктует код
      </span>
      <span v-if="codeInfo.deliveredBy === DeliveryType.FLASH_CALL">
        Введите код
      </span>
      <span v-if="codeInfo.deliveredBy === DeliveryType.SMS">
        Ожидайте sms с кодом
      </span>
    </h1>
    <div v-if="loginStore.phone" class="phone">
      <p
        v-if="codeInfo.deliveredBy === DeliveryType.FLASH_CALL"
        class="phone__headline"
      >
        Вам поступит звонок с уникального номера на
      </p>
      <div class="phone__wrapper">
        <div class="phone-number">
          <span class="phone-number">
            +{{ optionsPhone.code }} {{ loginStore.phone }}
          </span>
        </div>
        <app-button id="changePhone" variant="text" @click="changePhoneHandler">
          Изменить
        </app-button>
      </div>
      <p
        v-if="codeInfo.deliveredBy === DeliveryType.FLASH_CALL"
        class="confirm-title__description"
      >
        Введите последние
        <b>4 цифры этого номера</b>
      </p>
    </div>

    <label class="label" :for="form.code.id">{{ form.code.label }}</label>
    <app-input
      :id="form.code.id"
      v-model="value"
      v-maska:[optionsCode]
      class="input code"
      solo
      flat
      autofocus
      :maxlength="verificationCodeLength"
      autocomplete="off"
      :error-messages="errorMessage"
      :loading="confirmCode.inProgress"
      :disabled="confirmCode.inProgress || confirmCode.confirmed"
      @keydown="confirmCodeHandler"
    >
      <template #append-inner>
        <nuxt-img v-if="confirmCode.confirmed" src="/success-auth-icon.svg" />
      </template>
    </app-input>
    <app-button
      id="requestCode"
      :loading="requestCodeState.inProgress"
      block
      :disabled="requestCodeState.disabled"
      @click="repeatRequestCodeHandler"
    >
      <span>
        <span>Запросить код повторно</span>
        <span v-if="requestCodeState.timer > 0" class="timer">
          через {{ formatTime }}
        </span>
      </span>
    </app-button>
  </v-form>
</template>

<style lang="scss" scoped>
.form {
  max-width: 320px;
  margin: auto;
}

.confirm-title {
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 800;
  text-align: center;

  &__description {
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }
}

.label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
}

.input {
  margin-bottom: 0;
  font-size: 16px;
  font-weight: 600;
}

#requestCode {
  height: 50px;
  margin-top: 20px;
  font-size: 14px;
  font-weight: 700;
  line-height: 19px;
  text-transform: none;
  letter-spacing: 0;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(255, 80, 0, 0.16);
}

.phone {
  margin: 30px 0;
  text-align: center;

  &__headline {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }

  &__wrapper {
    display: flex;
    gap: 0 10px;
    align-items: center;
    justify-content: center;
  }

  .phone-number {
    font-size: 14px;
    font-weight: 600;
  }

  #changePhone {
    height: 24px;
    min-height: 24px;
    font-size: 12px;
    font-weight: 600;
    color: $primary;
  }
}
</style>
